import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { Icone } from '@/components/ui/Icone'

/**
 * Le rafraîchissement du Fil, atteignable depuis l'en-tête — RG-2.13ter, §88.
 *
 * **Pourquoi un canal et non une prop.** Le bouton vit dans l'en-tête de la
 * coque, au-dessus du Fil ; le geste, lui, appartient au Fil, qui seul sait
 * effacer ce que la séance a accumulé. Les deux sont séparés par toute la
 * hiérarchie, et faire descendre `onRafraichir` depuis `App` obligerait la
 * coque à connaître le Fil — exactement ce que le visage a déjà résolu de
 * cette façon.
 *
 * **`brancher` DOIT être stable, et c'est la seule chose qui compte ici.**
 * Une première version la reconstruisait à chaque changement d'état : le Fil
 * s'enregistrait, l'état changeait, l'identité de `brancher` changeait, l'effet
 * du Fil se rejouait, il se réenregistrait — une boucle infinie qui saturait
 * l'onglet et figeait toute l'application. Le contexte porte donc DEUX valeurs
 * de nature différente : ce qui change (le geste, l'attente) et ce qui ne
 * change jamais (la fonction d'enregistrement).
 */
const Canal = createContext<{
  rafraichir: (() => void) | null
  enCours: boolean
  brancher: (geste: (() => void) | null, enCours: boolean) => void
}>({
  rafraichir: null,
  enCours: false,
  brancher: () => undefined,
})

export function FournisseurDuFil({ children }: { children: ReactNode }) {
  const [etat, setEtat] = useState<{ rafraichir: (() => void) | null; enCours: boolean }>({
    rafraichir: null,
    enCours: false,
  })

  // `useCallback` sans dépendance : l'identité ne bouge JAMAIS. La mise à jour
  // passe par la forme fonctionnelle de `setEtat`, qui n'a pas besoin de lire
  // l'état courant depuis la closure.
  const brancher = useCallback((geste: (() => void) | null, enCours: boolean): void => {
    setEtat((avant) =>
      // On ne remplace l'objet que s'il change vraiment : sans ce garde, chaque
      // enregistrement déclencherait un rendu de toute la coque.
      avant.rafraichir === geste && avant.enCours === enCours
        ? avant
        : { rafraichir: geste, enCours },
    )
  }, [])

  const valeur = useMemo(
    () => ({ rafraichir: etat.rafraichir, enCours: etat.enCours, brancher }),
    [etat, brancher],
  )

  return <Canal.Provider value={valeur}>{children}</Canal.Provider>
}

/**
 * Branche le geste de rafraîchissement tant que le composant est monté.
 *
 * **Le geste passe par un `ref`.** C'est une closure : elle change à chaque
 * rendu du Fil, et l'inscrire telle quelle relancerait l'effet à chaque fois.
 * Ce qui est publié est une enveloppe stable, créée une seule fois, qui lit le
 * ref au moment du clic.
 */
export function useRafraichissementDuFil(geste: () => void, enCours: boolean): void {
  const { brancher } = useContext(Canal)
  const dernier = useRef(geste)

  useEffect(() => {
    dernier.current = geste
  })

  // Créée UNE fois pour la vie du composant : sans cela, chaque rendu du Fil
  // produirait une nouvelle fonction, l'effet ci-dessous se rejouerait, et le
  // provider recevrait un objet neuf à chaque tour — la boucle infinie qui
  // figeait l'application.
  const stable = useCallback(() => {
    dernier.current()
  }, [])

  useEffect(() => {
    brancher(stable, enCours)

    return () => {
      brancher(null, false)
    }
  }, [brancher, stable, enCours])
}

/**
 * Le bouton de l'en-tête — une ICÔNE, §88.
 *
 * **Ce que l'ancienne pilule coûtait.** « → Actualiser le fil » s'affichait en
 * permanence, centrée au-dessus de la première carte : la commande la plus rare
 * de l'écran occupait la place la plus visible, et poussait le contenu vers le
 * bas à chaque page.
 *
 * Le mot n'apprend rien à qui voit la flèche circulaire, et rien non plus à qui
 * ne la voit pas — c'est `aria-label` qui s'en charge.
 */
export function RafraichirDepuisLEntete() {
  const { rafraichir, enCours } = useContext(Canal)

  if (rafraichir === null) return null

  return (
    <button
      type="button"
      onClick={rafraichir}
      disabled={enCours}
      aria-label={enCours ? 'Actualisation en cours' : 'Actualiser le fil'}
      className="flex size-9 items-center justify-center rounded-full disabled:opacity-60"
      style={{ color: 'var(--accent-fort)' }}
    >
      <span
        // Le tour de la flèche pendant l'attente : sans lui, rien ne distingue
        // un rafraîchissement en cours d'un bouton qui n'a pas répondu.
        className={enCours ? 'animate-spin' : undefined}
      >
        <Icone nom="rafraichir" taille={20} />
      </span>
    </button>
  )
}
